import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, Network } from "lucide-react";
import { adjacentEngagement, getExperience, getProject, getProjectsForExperience } from "../lib/queries";
import { PROJECT_SLUG_ALIASES } from "../lib/slug-aliases";
import { BrandLogo } from "../lib/brands";
import { BookmarkButton } from "../components/BookmarkButton";
import { TalkToMeButton } from "../components/ChatPanel";
import {
  AskAbout,
  BackBar,
  IndustryChip,
  NotFound,
  SkillChip,
  TagRow,
  TechChip,
  paragraphs,
} from "../components/DetailBits";
import { authEnabled } from "../auth/config";
import { AdminEdit } from "../components/EngagementEditor";
import { ShareThoughts } from "../components/ShareThoughts";
import { getStage, useOverlayVersion } from "../lib/overlay";

export function Project() {
  const { id } = useParams<{ id: string }>();
  useOverlayVersion(); // re-render when an admin edit lands or admin-mode toggles
  const canonical = id ? PROJECT_SLUG_ALIASES[id] : undefined;
  if (canonical) return <Navigate to={`/project/${canonical}`} replace />;
  const p = id ? getProject(id) : undefined;
  if (!p) return <NotFound />;

  const e = getExperience(p.experienceId);
  const siblings = getProjectsForExperience(p.experienceId)
    .filter((x) => x.id !== p.id)
    .slice(0, 4);
  const isDraft = getStage(p.id, p.status) === "draft";
  const { prev, next } = adjacentEngagement(p.id);

  return (
    <article className="detail wrap-narrow section">
      <BackBar to={e ? `/experience/${e.id}` : "/"} label={e ? e.company : "Back"} />
      <header className="proj-head">
        <div className="proj-crumb">
          {e && (
            <Link to={`/experience/${e.id}`} className="proj-crumb-co">
              <BrandLogo company={e.company} size={26} /> {e.company}
            </Link>
          )}
          <span className="eyebrow">Engagement</span>
          {isDraft && (
            <span className="now-tag mono">Narrative draft — pending Srinath's edit</span>
          )}
        </div>
        <div className="title-row">
          <h1 className="display proj-title">{p.title}</h1>
          <BookmarkButton nodeType="project" nodeId={p.id} title={p.title} />
        </div>
        <p className="proj-summary">{p.summary}</p>
        {p.clientAnonymized && <p className="proj-client mono">Client · {p.clientAnonymized}</p>}
      </header>

      {authEnabled && <AdminEdit key={p.id} project={p} />}

      {p.metrics && p.metrics.length > 0 && (
        <div className="metric-row">
          {p.metrics.map((m) => (
            <div className="metric" key={m.label}>
              <div className="display metric-v">{m.value}</div>
              <div className="eyebrow">{m.label}</div>
            </div>
          ))}
        </div>
      )}

      <div className="detail-actions">
        <AskAbout prompt={`Tell me more about the "${p.title}" engagement.`} />
        <TalkToMeButton
          context={{
            nodeType: "project",
            nodeId: p.id,
            nodeTitle: p.title,
            nodeSummary: p.summary,
          }}
        />
        <Link to={`/?cview=graph&gfocus=project:${p.id}`} className="btn btn-sm">
          <Network size={15} /> See in graph
        </Link>
      </div>

      {p.narrative && <div className="detail-body">{paragraphs(p.narrative)}</div>}

      <div className="detail-tags">
        <TagRow label="Industries">
          {p.industries.map((i) => (
            <IndustryChip key={i} id={i} />
          ))}
        </TagRow>
        <TagRow label="Technologies">
          {p.technologies.map((tx) => (
            <TechChip key={tx} id={tx} />
          ))}
        </TagRow>
        <TagRow label="Skills">
          {p.skills.map((s) => (
            <SkillChip key={s} id={s} />
          ))}
        </TagRow>
      </div>

      {e && siblings.length > 0 && (
        <section className="detail-section">
          <div className="section-head">
            <h2 className="display" style={{ fontSize: 24 }}>
              More from {e.company}
            </h2>
          </div>
          <div className="sib-grid">
            {siblings.map((s) => (
              <Link key={s.id} to={`/project/${s.id}`} className="sib-card card card-link card-pad">
                <div className="sib-title">{s.title}</div>
                <ArrowUpRight size={15} className="muted" />
              </Link>
            ))}
          </div>
        </section>
      )}
      {(prev || next) && (
        <nav className="detail-pager" aria-label="Engagement pagination">
          {prev ? (
            <Link to={`/project/${prev.id}`} className="detail-pager-link">
              <ArrowLeft size={16} />
              <span className="detail-pager-text">
                <span className="detail-pager-kicker mono">Previous engagement</span>
                <span className="detail-pager-name">{prev.title}</span>
              </span>
            </Link>
          ) : (
            <span />
          )}
          {next ? (
            <Link to={`/project/${next.id}`} className="detail-pager-link detail-pager-next">
              <span className="detail-pager-text">
                <span className="detail-pager-kicker mono">Next engagement</span>
                <span className="detail-pager-name">{next.title}</span>
              </span>
              <ArrowRight size={16} />
            </Link>
          ) : (
            <span />
          )}
        </nav>
      )}
      <ShareThoughts className="page-share-bottom" />
    </article>
  );
}
